import posthog from 'posthog-js'
import { scrubPostHogEvent, scrubStoredUrls } from '@/utils/url-credentials'

export function usePostHog() {
  const initPostHog = () => {
    if (typeof window !== 'undefined' && !posthog.__loaded) {
      const posthogKey = import.meta.env.VITE_POSTHOG_KEY || 'phc_56U4KeixI2hoAzPZb8ZWkNaa23uRV04G4g1OzIHxUlB'
      posthog.init(posthogKey, {
        api_host: 'https://k.moshealth.com',
        ui_host: 'https://us.posthog.com',
        person_profiles: 'identified_only',
        capture_pageview: false, // We'll handle this manually with router
        capture_pageleave: true,
        autocapture: true,
        disable_session_recording: false,
        // Replay records layout and interaction, never words: every text node
        // and every input value is masked. These local values take precedence
        // over the project's "Privacy and masking" setting. Network request and
        // response headers and bodies are never recorded; a local `false` wins
        // over the project's network capture setting. Request timing is unaffected.
        session_recording: {
          maskAllInputs: true,
          maskTextSelector: '*',
          maskInputOptions: {
            password: true,
          },
          recordHeaders: false,
          recordBody: false,
        },
        // Remove auth tokens and codes from URLs (Supabase sign-in fragment,
        // `?code=`, `?token=`): on every event, replay included, and in the
        // URLs the SDK stores and sends with `/flags`.
        before_send: scrubPostHogEvent,
        loaded: scrubStoredUrls,
      })
    }
  }

  const identifyUser = (userId: string, properties?: Record<string, any>) => {
    if (typeof window !== 'undefined') {
      posthog.identify(userId, properties)
    }
  }

  const trackEvent = (eventName: string, properties?: Record<string, any>) => {
    if (typeof window !== 'undefined') {
      posthog.capture(eventName, properties)
    }
  }

  const setUserProperties = (properties: Record<string, any>) => {
    if (typeof window !== 'undefined') {
      posthog.people.set(properties)
    }
  }

  const resetUser = () => {
    if (typeof window !== 'undefined') {
      posthog.reset()
    }
  }

  const capturePageView = (pageName?: string) => {
    if (typeof window !== 'undefined') {
      posthog.capture('$pageview', {
        page: pageName || window.location.pathname,
      })
    }
  }

  return {
    posthog,
    initPostHog,
    identifyUser,
    trackEvent,
    setUserProperties,
    resetUser,
    capturePageView,
  }
}
